import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useNavigation } from "../../utilities/NavigationContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

class Register extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirm: "",
      userType: "",
      role: "user",
      q1: "",
      q1Detail: "",
      q2Detail: "",
      q3: "",
      q3Detail: "",
      passwordMatch: true,
      isSubmitting: false,
      company: "",
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "password" || name === "passwordConfirm") {
      const match =
        name === "password"
          ? value === this.state.passwordConfirm
          : value === this.state.password;

      this.setState({ [name]: value, passwordMatch: match });
      return;
    }

    this.setState({ [name]: value });
  };

  buildCombinedComment = () => {
    const { q1, q1Detail, q2Detail, q3, q3Detail } = this.state;

    return [
      `1) Are you presently overseeing patients with multiple chronic conditions and complex consultations? Answer: ${
        q1 || "not answered"
      }`,
      q1Detail?.trim() ? `   Details: ${q1Detail.trim()}` : null,

      `2) What kind of care plan strategies do you use?`,
      q2Detail?.trim() ? `   ${q2Detail.trim()}` : null,

      `3) Do you think a live / always-up-to-date care plan is important? Answer: ${
        q3 || "not answered"
      }`,
      q3Detail?.trim() ? `   Details: ${q3Detail.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirm,
      userType,
      role,
      q1,
      q3,
      company,
    } = this.state;

    if (company && company.trim().length > 0) return;

    if (!firstName.trim()) return toast.error("Please enter your first name.");
    if (!lastName.trim()) return toast.error("Please enter your last name.");
    if (!email.trim()) return toast.error("Please enter your email.");
    if (!userType.trim()) return toast.error("Please select your role.");
    if (!password) return toast.error("Please enter a password.");
    if (password !== passwordConfirm) {
      return toast.error("Passwords do not match!");
    }
    if (!q1 || !q3) {
      return toast.error("Please answer Yes/No for questions 1 and 3.");
    }

    const comment = this.buildCombinedComment();
    if (!comment.trim()) return toast.error("Please add some comments.");

    const { executeRecaptcha } = this.props;
    if (!executeRecaptcha) {
      return toast.error("reCAPTCHA is loading—please try again.");
    }

    this.setState({ isSubmitting: true });

    let tokenCap;
    try {
      tokenCap = await executeRecaptcha("subscribe");
    } catch (err) {
      this.setState({ isSubmitting: false });
      return toast.error("reCAPTCHA failed—please try again.");
    }

    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        passwordConfirm,
        userType: userType.trim(),
        role,
        comment,
        tokenCap,
        company,
      };

      const res = await axios.post(`${EndPoint}/user`, payload);

      toast.success(res.data?.message || "Registered successfully!");
      this.props.navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error registering");
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  renderQuestionCard(title, children) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
        <p className="text-sm font-semibold leading-6 text-slate-800">{title}</p>
        <div className="mt-4">{children}</div>
      </div>
    );
  }

  renderRadioGroup(name, value) {
    return (
      <div className="flex flex-wrap gap-3">
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
            value === "yes"
              ? "border-sky-300 bg-sky-100 text-sky-800"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          <input
            type="radio"
            name={name}
            value="yes"
            checked={value === "yes"}
            onChange={this.handleInputChange}
            className="sr-only"
          />
          Yes
        </label>

        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
            value === "no"
              ? "border-sky-300 bg-sky-100 text-sky-800"
              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
          }`}
        >
          <input
            type="radio"
            name={name}
            value="no"
            checked={value === "no"}
            onChange={this.handleInputChange}
            className="sr-only"
          />
          No
        </label>
      </div>
    );
  }

  render() {
    const {
      firstName,
      lastName,
      email,
      password,
      passwordConfirm,
      userType,
      q1,
      q1Detail,
      q2Detail,
      q3,
      q3Detail,
      isSubmitting,
      company,
      passwordMatch,
    } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-900">
        <ToastContainer />

        <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 py-10">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-xl lg:grid-cols-[0.95fr_1.05fr]">
            {/* Left panel */}
            <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-900 via-cyan-800 to-slate-900 p-10 lg:flex lg:flex-col lg:justify-between">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.2),transparent_30%)]" />

              <div className="relative z-10">
                <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100">
                  Caredevo
                </div>

                <h1 className="mt-6 text-4xl font-bold leading-tight text-white">
                  Create your account
                </h1>

                <p className="mt-4 max-w-md text-sm leading-7 text-sky-100/90">
                  Join Caredevo to explore safer, more structured chronic care
                  workflows and help shape better live care planning.
                </p>
              </div>

              <div className="relative z-10 grid gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Built for clinicians
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Capture your role and workflow so the platform fits real-world
                    clinical use.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Protected sign-up
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Includes reCAPTCHA and anti-bot protection during registration.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Tell us your workflow
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Your answers help tailor the experience and guide product
                    priorities.
                  </p>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="bg-white px-6 py-10 sm:px-10 lg:px-12">
              <div className="mx-auto w-full max-w-2xl">
                <div className="mb-8 lg:hidden">
                  <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                    Caredevo
                  </div>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                  Registration
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Create your account and tell us a bit about your care planning
                  workflow.
                </p>

                <form onSubmit={this.handleSubmit} className="mt-8 space-y-6">
                  {/* Basic details */}
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        placeholder="First Name"
                        value={firstName}
                        onChange={this.handleInputChange}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={this.handleInputChange}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={this.handleInputChange}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Password
                      </label>
                      <input
                        type="password"
                        name="password"
                        placeholder="Create a password"
                        value={password}
                        onChange={this.handleInputChange}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-700">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        name="passwordConfirm"
                        placeholder="Confirm your password"
                        value={passwordConfirm}
                        onChange={this.handleInputChange}
                        className={`w-full rounded-2xl px-4 py-3 text-sm text-slate-900 outline-none transition focus:bg-white focus:ring-4 ${
                          passwordConfirm && !passwordMatch
                            ? "border border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100"
                            : "border border-slate-200 bg-slate-50 focus:border-sky-400 focus:ring-sky-100"
                        }`}
                      />
                      {passwordConfirm && !passwordMatch && (
                        <p className="mt-2 text-xs font-medium text-red-600">
                          Passwords do not match.
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Role
                    </label>
                    <select
                      name="userType"
                      value={userType}
                      onChange={this.handleInputChange}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    >
                      <option value="" disabled>
                        Select your Role
                      </option>
                      <option value="GP">GP</option>
                      <option value="Specialist">Specialist</option>
                      <option value="Allied Health">Allied Health</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  {/* Question 1 */}
                  {this.renderQuestionCard(
                    "1) Are you presently overseeing patients with multiple chronic conditions and complex consultations?",
                    <>
                      {this.renderRadioGroup("q1", q1)}
                      <textarea
                        name="q1Detail"
                        value={q1Detail}
                        onChange={this.handleInputChange}
                        placeholder="Brief details or examples..."
                        className="mt-4 h-24 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                      />
                    </>
                  )}

                  {/* Question 2 */}
                  {this.renderQuestionCard(
                    "2) What kind of care plan strategies do you use (e.g. update every consultation, separate notes, none)?",
                    <textarea
                      name="q2Detail"
                      value={q2Detail}
                      onChange={this.handleInputChange}
                      placeholder="Describe your current strategy..."
                      className="h-24 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                    />
                  )}

                  {/* Question 3 */}
                  {this.renderQuestionCard(
                    "3) Do you think a live / always-up-to-date care plan is important? If yes, why?",
                    <>
                      {this.renderRadioGroup("q3", q3)}
                      <textarea
                        name="q3Detail"
                        value={q3Detail}
                        onChange={this.handleInputChange}
                        placeholder="Why or why not?"
                        className="mt-4 h-24 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                      />
                    </>
                  )}

                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label>
                      Company
                      <input
                        type="text"
                        name="company"
                        value={company}
                        onChange={this.handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </label>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition ${
                        isSubmitting
                          ? "cursor-not-allowed bg-slate-400"
                          : "bg-sky-700 hover:bg-sky-800"
                      }`}
                    >
                      {isSubmitting ? "Creating..." : "Create Account"}
                    </button>
                  </div>

                  <div className="border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="font-semibold text-sky-700 transition hover:text-sky-900"
                    >
                      Login here
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const RegisterWithNavigation = (props) => {
  const navigate = useNavigation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <Register
      {...props}
      navigate={navigate}
      executeRecaptcha={executeRecaptcha}
    />
  );
};

export default RegisterWithNavigation;